import { useEffect, useMemo } from 'react';
import { InlineLoading, RadioButton, RadioButtonGroup } from '@carbon/react';
import {
  NumberField,
  TextField,
  TextAreaField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { FreeTextComboBox } from 'components/free-text-combobox/free-text-combobox';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import {
  AreaTypeFields,
  UnitTypeFields,
} from 'components/scenario-area-configs';
import type {
  AreaTypeFieldsAtoms,
  ScenarioPickerForm,
  UnitTypeFieldsAtoms,
} from 'components/scenario-area-configs';
import { useScenarioForm } from './contexts';
import {
  categoriesLoadableAtom,
  unitTypesLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  areaSearchLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  unitSearchLoadableAtom,
  fetchGeometryLoadableAtom,
  areaGeometriesAtom,
  toastAtom,
  slotSearchParamsAtom,
  slotSearchLoadableAtom,
  SLOT_QUERY_MIN_LENGTH,
} from './atoms';
import { getPaceParamLabel, validatePaceValue } from './utils';
import styles from './area-configs.module.scss';

/** The area and unit fields are the shared ones; they read this form's own copies of the atoms. */
const AREA_FIELD_ATOMS: AreaTypeFieldsAtoms = {
  categoriesLoadableAtom,
  areaSearchLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  fetchGeometryLoadableAtom,
  areaGeometriesAtom,
  toastAtom,
};

const UNIT_FIELD_ATOMS: UnitTypeFieldsAtoms = {
  unitTypesLoadableAtom,
  unitSearchLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  fetchGeometryLoadableAtom,
  areaGeometriesAtom,
  toastAtom,
};

interface AreaConfigsProps {
  areaIndex: number;
}

/**
 * Area configuration form section. Rendered when an area is selected
 * in the sidebar. Reads/writes form state via useScenarioForm() context.
 * @param {AreaConfigsProps} props - The component props
 * @return {JSX.Element} The area configs form
 */
export function AreaConfigs({ areaIndex }: AreaConfigsProps): JSX.Element {
  const { form, markAreaConfigured } = useScenarioForm();
  const prefix = `areas[${areaIndex}]` as const;
  const setGeometries = useSetAtom(areaGeometriesAtom);
  // The shared pickers read the form by path name, not by this form's typed field names.
  // eslint-disable-next-line no-restricted-syntax -- TanStack's per-form field-name union cannot take the pickers' path strings
  const pickerForm = form as unknown as ScenarioPickerForm;

  return (
    <div className={styles.areaConfigs}>
      <div className={styles.areaRow}>
        <div className={styles.detailsCol}>
          <h4 className={styles.sectionTitle}>Area Details</h4>

          <form.Field name={`${prefix}.geometryType`}>
            {(field: any) => (
              <RadioButtonGroup
                legendText="Geometry Type (required)"
                name={`geometry-type-${areaIndex}`}
                valueSelected={field.state.value}
                orientation="horizontal"
                onChange={(value: string | number | undefined) => {
                  const entryId = form.getFieldValue(`${prefix}.id`);
                  field.handleChange(value);
                  // Switching geometry type clears every other kind's fields, so a
                  // half-filled slot/area/unit never rides along on save.
                  form.setFieldValue(`${prefix}.configurableId`, null);
                  form.setFieldValue(`${prefix}.category`, '');
                  form.setFieldValue(`${prefix}.areaName`, '');
                  form.setFieldValue(`${prefix}.unitType`, '');
                  form.setFieldValue(`${prefix}.unitName`, '');
                  form.setFieldValue(`${prefix}.slotId`, '');
                  form.setFieldValue(`${prefix}.slotLabel`, '');
                  if (entryId) {
                    setGeometries((prev) => {
                      const next = { ...prev };
                      delete next[entryId];
                      return next;
                    });
                  }
                }}
              >
                <RadioButton
                  id={`geom-area-${areaIndex}`}
                  labelText="Area"
                  value="area"
                />
                <RadioButton
                  id={`geom-unit-${areaIndex}`}
                  labelText="Unit"
                  value="unit"
                />
                <RadioButton
                  id={`geom-slot-${areaIndex}`}
                  labelText="Property"
                  value="slot"
                />
              </RadioButtonGroup>
            )}
          </form.Field>

          <form.Subscribe
            selector={(state) => state.values.areas[areaIndex]?.geometryType}
          >
            {(geometryType) => {
              if (geometryType === 'area') {
                return (
                  <AreaTypeFields
                    form={pickerForm}
                    areaIndex={areaIndex}
                    atoms={AREA_FIELD_ATOMS}
                    categoryLabel="Choose an option"
                    wrapAreaOptions={false}
                  />
                );
              }
              if (geometryType === 'slot') {
                return <PropertyTypeFields areaIndex={areaIndex} />;
              }
              return (
                <UnitTypeFields
                  form={pickerForm}
                  areaIndex={areaIndex}
                  atoms={UNIT_FIELD_ATOMS}
                />
              );
            }}
          </form.Subscribe>

          <div className={styles.halfWidth}>
            <form.Field name={`${prefix}.alias`}>
              {(field: any) => (
                <TanstackFieldWrapper<string> field={field}>
                  {(props) => (
                    <TextField
                      id={`alias-${areaIndex}`}
                      labelText="Alias"
                      placeholder="Optional display name"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
        </div>

        <div className={styles.notesCol}>
          <h4 className={styles.sectionTitle}>Notes</h4>
          <form.Field name={`${prefix}.notes`}>
            {(field: any) => (
              <TanstackFieldWrapper<string> field={field}>
                {(props) => (
                  <TextAreaField
                    id={`area-notes-${areaIndex}`}
                    labelText=""
                    placeholder="Add notes about this area..."
                    rows={6}
                    {...props}
                  />
                )}
              </TanstackFieldWrapper>
            )}
          </form.Field>
        </div>
      </div>

      <h4 className={styles.sectionTitle}>Development Pace Parameters</h4>
      <form.Subscribe selector={(state) => state.values.global.paceModel}>
        {(paceModel) => (
          <div className={styles.paramsGrid}>
            <form.Field
              name={`${prefix}.paceModelValue`}
              validators={{
                // onMount too: the value carries over when the pace model is
                // switched from the Global panel, so the field must re-check
                // itself against the new model as soon as it re-mounts.
                onMount: ({ value }: { value: number | string }) =>
                  validatePaceValue(paceModel)(value),
                onChange: ({ value }: { value: number | string }) =>
                  validatePaceValue(paceModel)(value),
              }}
            >
              {(field: any) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    // NumberField, not Carbon's NumberInput (ONCALL-840). A
                    // native input[type=number] reports value === '' for the
                    // in-progress string "6.", and NumberInput's allowEmpty
                    // defaults to false, so that keystroke committed
                    // Number('') === 0 and erased the decimal point — typing
                    // "6.5" landed on 5. NumberField keeps a local display
                    // string across re-renders, which is the whole reason it
                    // exists (see its own comment).
                    //
                    // Deliberately NOT constrained by `min` or `isAllowed`:
                    // both reject at the keystroke, so a `>= 1` guard would
                    // block the leading "0" of "0.5" and recreate this exact
                    // bug in a new costume. The [1, 50] bound belongs to
                    // validatePaceValue, which reports it via invalidText.
                    // `decimalScale` is likewise left unset — capping it would
                    // silently truncate a value the numeric column accepts.
                    //
                    // Known UX delta: NumberField renders a text input, so this
                    // field loses the up/down steppers that the nine lifecycle
                    // fields below still have. Unavoidable while a decimal must
                    // be typeable, and worth expecting in UAT.
                    <NumberField
                      id={`area-pace-value-${areaIndex}`}
                      label={getPaceParamLabel(paceModel)}
                      light
                      thousandSeparator={false}
                      allowNegative={false}
                      {...props}
                      // NumberField speaks number | null; the validator and the
                      // payload speak number | ''. Bridge in both directions so
                      // a cleared field still reports "This field is required"
                      // (Number(null) === 0 would downgrade it to a range
                      // error) and so null never reaches buildCreatePayload,
                      // where paceModelValue is typed number.
                      value={
                        field.state.value === '' ? null : field.state.value
                      }
                      onChange={(next: number | null) => {
                        markAreaConfigured(
                          form.getFieldValue(`${prefix}.id`) as number
                        );
                        field.handleChange(next === null ? '' : next);
                      }}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
        )}
      </form.Subscribe>
    </div>
  );
}

/**
 * Property-type fields: a single search-first combobox over the scenario's
 * paceable universe (2.1). Enabled only once a Type Curve Scenario is selected.
 * @param {{ areaIndex: number }} props - The area index
 * @return {JSX.Element} The property (slot) type fields
 */
function PropertyTypeFields({ areaIndex }: { areaIndex: number }): JSX.Element {
  const { form } = useScenarioForm();

  // typeCurveScenarioId drives both the enabled state and the query, so read it
  // reactively and hand it to the field component (which owns the debounce hook).
  return (
    <form.Subscribe
      selector={(state) => state.values.global.typeCurveScenarioId}
    >
      {(typeCurveScenarioId) => (
        <SlotSearchField
          areaIndex={areaIndex}
          typeCurveScenarioId={typeCurveScenarioId}
        />
      )}
    </form.Subscribe>
  );
}

/**
 * The slot typeahead itself. 300ms debounce, min-2-char gate, in-flight spinner,
 * and distinct empty/disabled copy — the field is inert until a Type Curve
 * Scenario is chosen (matching the form's capture gating).
 * @param {object} props - The area index and the selected TC scenario id.
 * @return {JSX.Element} The slot search field
 */
function SlotSearchField({
  areaIndex,
  typeCurveScenarioId,
}: {
  areaIndex: number;
  typeCurveScenarioId: number | null;
}): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}]` as const;
  const slotSearchState = useAtomValue(slotSearchLoadableAtom);
  const { query: activeQuery } = useAtomValue(slotSearchParamsAtom);
  const setSlotSearchParams = useSetAtom(slotSearchParamsAtom);
  const setToast = useSetAtom(toastAtom);
  const disabled = typeCurveScenarioId === null;

  useEffect(() => {
    if (slotSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search slots', type: 'error' });
    }
  }, [slotSearchState.state, setToast]);

  const debouncedQuery = useMemo(
    () =>
      debounce((text: string) => {
        setSlotSearchParams({ typeCurveScenarioId, query: text });
      }, 300),
    [setSlotSearchParams, typeCurveScenarioId]
  );

  const slotData =
    slotSearchState.state === 'hasData' ? slotSearchState.data : null;
  const slotItems = useMemo(
    () =>
      (slotData?.slots ?? []).map((slot) => ({
        id: slot.slotId,
        value: slot.slotId,
        label: slot.label,
      })),
    [slotData]
  );
  // Only a query that actually reached the 2-char threshold reads as "no match";
  // shorter input is a silent no-op on both sides (spec §5/§7), never an error copy.
  const searched = (activeQuery ?? '').trim().length >= SLOT_QUERY_MIN_LENGTH;
  const showEmptyCopy =
    !disabled && searched && !!slotData && slotItems.length === 0;

  return (
    <div className={styles.formGrid}>
      <div className={styles.typeaheadWrapper}>
        <form.Field name={`${prefix}.slotId`}>
          {(field: any) => {
            const label = form.getFieldValue(`${prefix}.slotLabel`) as string;
            return (
              <FreeTextComboBox
                id={`slot-selection-${areaIndex}`}
                titleText="Slot ID (required)"
                placeholder="Search paceable slots..."
                disabled={disabled}
                items={slotItems}
                selectedItem={
                  field.state.value
                    ? {
                        value: field.state.value,
                        label: label || field.state.value,
                      }
                    : null
                }
                itemToString={(item) => item?.label ?? ''}
                onChange={({ selectedItem }) => {
                  field.handleChange(selectedItem?.id ?? '');
                  form.setFieldValue(
                    `${prefix}.slotLabel`,
                    selectedItem?.label ?? ''
                  );
                }}
                onInputChange={(text) => {
                  if (!disabled) debouncedQuery(text);
                }}
              />
            );
          }}
        </form.Field>
        {slotSearchState.state === 'loading' && (
          <InlineLoading description="" className={styles.spinner} />
        )}
      </div>
      {disabled && (
        <p className={styles.slotHint}>Pick a Type Curve Scenario first</p>
      )}
      {showEmptyCopy && (
        <p className={styles.slotHint}>No paceable slots match</p>
      )}
    </div>
  );
}
